import { fetchAuthSession } from 'aws-amplify/auth';

const API_ENDPOINT = 'https://h1k1zfprsa.execute-api.us-east-1.amazonaws.com/handle-profile-asset-upload';

/**
 * Service for handling profile asset uploads (avatar and banner)
 */
const profileDataService = {
  /**
   * Upload a profile picture
   * @param {File} file - The image file to upload
   * @param {string} userHandle - User's handle/username
   * @returns {Promise<Object>} - Upload result
   */
  async uploadProfilePicture(file, userHandle) {
    return this.uploadProfileAsset(file, 'avatar', userHandle);
  },

  /**
   * Upload a profile banner
   * @param {File} file - The image file to upload
   * @param {string} userHandle - User's handle/username
   * @returns {Promise<Object>} - Upload result
   */
  async uploadProfileBanner(file, userHandle) {
    return this.uploadProfileAsset(file, 'banner', userHandle);
  },

  /**
   * Get the URL for a profile asset
   * @param {string} assetType - Type of asset ('avatar' or 'banner')
   * @param {string} userHandle - User's handle/username
   * @returns {string} - URL of the asset
   */
  getProfileAssetUrl(assetType, userHandle) {
    if (!userHandle || !assetType || !['avatar', 'banner'].includes(assetType)) {
      console.warn('Invalid parameters for getProfileAssetUrl', { assetType, userHandle });
      return null;
    }
    
    // Use timestamp to prevent caching
    const timestamp = Date.now();
    console.log(`Getting ${assetType} URL for user: ${userHandle}`);
    
    // Ensure correct asset type spelling (avatar.jpg or banner.jpg)
    const fileName = assetType === 'avatar' ? 'avatar.jpg' : 'banner.jpg';
    
    return `https://screamify-profile-assets.s3.us-east-1.amazonaws.com/${userHandle}/${fileName}?t=${timestamp}`;
  },

  /**
   * Check if an image exists at a URL
   * @param {string} url - The URL to check
   * @returns {Promise<boolean>} - True if image exists, false otherwise
   */
  async imageExists(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Error checking if image exists:', error);
      return false;
    }
  },

  /**
   * Upload a profile asset (avatar or banner)
   * @param {File} file - The image file to upload
   * @param {string} assetType - Type of asset ('avatar' or 'banner')
   * @param {string} userHandle - User's handle/username
   * @returns {Promise<Object>} - Upload result
   */
  async uploadProfileAsset(file, assetType, userHandle) {
    if (!file || !assetType || !['avatar', 'banner'].includes(assetType) || !userHandle) {
      throw new Error('Invalid parameters for profile asset upload');
    }

    try {
      // Check if file is too large (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image file is too large. Maximum allowed size is 5MB.');
      }

      // Convert any image to JPEG format
      const jpegBlob = await this.convertToJpeg(file);
      console.log(`Converted image to JPEG, size: ${jpegBlob.size} bytes`);
      
      // Get the auth token
      const session = await fetchAuthSession();
      const token = session?.tokens?.accessToken?.toString();
      
      if (!token) {
        throw new Error('Authentication required. Please sign in again.');
      }
      
      // Create the API URL with query parameters - now using userHandle instead of cognito_sub
      const url = `${API_ENDPOINT}?pic_type=${assetType}&user_handle=${encodeURIComponent(userHandle)}`;
      console.log(`Uploading ${assetType} for user ${userHandle} to URL: ${url}`);
      
      // Make the API call
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'image/jpeg',
          'Authorization': `Bearer ${token}`
        },
        body: jpegBlob
      });
      
      // Log the complete response for debugging
      console.log(`Upload response status: ${response.status}`);
      const responseText = await response.text();
      console.log(`Upload response body: ${responseText}`);
      
      // Check if the request was successful
      if (!response.ok) {
        console.error(`Error uploading ${assetType}. Status: ${response.status}, Body: ${responseText}`);
        throw new Error(`Failed to upload ${assetType}: ${response.status} - ${responseText}`);
      }
      
      // Parse and return the response
      try {
        const data = JSON.parse(responseText);
        return data;
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        return { message: responseText, success: response.ok };
      }
    } catch (error) {
      console.error(`Error uploading ${assetType}:`, error);
      throw error;
    }
  },

  /**
   * Convert any image file to JPEG format and resize if necessary
   * @param {File} file - The image file to convert
   * @returns {Promise<Blob>} - JPEG blob
   */
  async convertToJpeg(file) {
    return new Promise((resolve, reject) => {
      // Create image element
      const img = new Image();
      const reader = new FileReader();
      
      // Handle image load
      img.onload = () => {
        // Create canvas for conversion
        const canvas = document.createElement('canvas');
        
        // Determine if resizing is needed (max dimensions: 1200x1200)
        const MAX_DIMENSION = 1200;
        let width = img.width;
        let height = img.height;
        
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }
        
        // Set canvas dimensions to the resized image dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#000000'; // Black background for transparent images
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG blob
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('Failed to convert image to JPEG'));
            return;
          }
          resolve(blob);
        }, 'image/jpeg', 0.9); // 90% quality
      };
      
      // Handle errors
      img.onerror = () => {
        reject(new Error('Failed to load image for conversion'));
      };
      
      // Load image from file
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }
};

export default profileDataService; 